import { CropArea } from 'core/crop-area';
import { Region } from 'core/geometry';
import { BehaviorSubject } from 'rxjs';

import { BoundingBox, Dimensions } from './image-placeholder';

const TOLERANCE = 1e-6;

function sameMatrix(a: DOMMatrixReadOnly, b: DOMMatrixReadOnly): boolean
{
  return [a.a - b.a, a.b - b.b, a.c - b.c, a.d - b.d, a.e - b.e, a.f - b.f]
      .every((difference) => Math.abs(difference) < TOLERANCE);
}

export class TransformationManager
{
  public readonly changed: BehaviorSubject<void> =
      new BehaviorSubject<void>(undefined);

  private matrix: DOMMatrixReadOnly = new DOMMatrixReadOnly();
  private image: Region;
  private boundingBoxRegion: Region;

  constructor(
      imageDimensions: Dimensions,
      bbox: BoundingBox,
      matrix?: DOMMatrixReadOnly)
  {
    this.reset(imageDimensions, bbox, matrix);
  }

  public reset(
      imageDimensions: Dimensions,
      bbox: BoundingBox,
      matrix?: DOMMatrixReadOnly): void
  {
    this.image =
        new Region(0, 0, imageDimensions.width, imageDimensions.height);
    this.matrix = matrix === undefined ? new DOMMatrixReadOnly() : matrix;

    this.takeOver(Region.fromSimilar(bbox));
  }

  public applyBoundingBox(bbox: BoundingBox): void
  {
    const boundingBoxRegion = Region.fromSimilar(bbox);

    if (!boundingBoxRegion.equals(this.boundingBoxRegion)) {
      this.takeOver(boundingBoxRegion);
    }
  }

  private takeOver(boundingBoxRegion: Region): void
  {
    this.boundingBoxRegion = boundingBoxRegion;

    const corrected = this.corrected(this.matrix);

    if (!sameMatrix(corrected, this.matrix)) {
      this.matrix = corrected;
    }

    this.changed.next();
  }

  public translate(dx: number, dy: number): void
  {
    const inverse = this.matrix.inverse();
    const origin = inverse.transformPoint(new DOMPoint(0, 0));
    const moved = inverse.transformPoint(new DOMPoint(dx, dy));

    this.applyMatrix(
        this.matrix.translate(origin.x - moved.x, origin.y - moved.y));
  }

  public scale(ds: number): void
  {
    const center = this.boundingBoxRegion.center;

    this.applyMatrix(
        new DOMMatrixReadOnly()
            .scale(ds, ds, 1, center.x, center.y)
            .multiply(this.matrix));
  }

  public rotate(da: number): void
  {
    const center = this.boundingBoxRegion.center;

    this.applyMatrix(
        new DOMMatrixReadOnly()
            .translate(center.x, center.y)
            .rotate(da)
            .translate(-center.x, -center.y)
            .multiply(this.matrix));
  }

  public getMatrix(): DOMMatrixReadOnly
  {
    return this.matrix;
  }

  public getRotation(): number
  {
    let deg = Math.atan2(this.matrix.b, this.matrix.a) * (180 / Math.PI);
    if (deg < 0) {
      deg = 360 + deg % 360;
    }
    if (deg >= 360) {
      deg = deg % 360;
    }

    return deg;
  }

  public getScale(): number
  {
    return 1 / this.coverScaleOf(this.matrix);
  }

  public getMagnification(): number
  {
    return Math.hypot(this.matrix.a, this.matrix.b);
  }

  private coverScaleOf(matrix: DOMMatrixReadOnly): number
  {
    return new CropArea(this.image, matrix)
        .coverScaleFor(this.boundingBoxRegion);
  }

  private applyMatrix(matrix: DOMMatrixReadOnly): void
  {
    const corrected = this.corrected(this.limitedToZoom(matrix));

    if (sameMatrix(corrected, this.matrix)) {
      return;
    }

    this.matrix = corrected;
    this.changed.next();
  }

  private corrected(matrix: DOMMatrixReadOnly): DOMMatrixReadOnly
  {
    const magnified = this.magnifiedToCover(matrix);
    const shown = this.boundingBoxRegion;
    const fitted =
        new CropArea(this.image, magnified).clampSelection(shown);

    return new DOMMatrixReadOnly()
        .translate(shown.x - fitted.x, shown.y - fitted.y)
        .multiply(magnified);
  }

  private magnifiedToCover(matrix: DOMMatrixReadOnly): DOMMatrixReadOnly
  {
    const cover = this.coverScaleOf(matrix);

    return cover <= 1 + TOLERANCE
        ? matrix
        : this.scaledAbout(matrix, cover);
  }

  private limitedToZoom(matrix: DOMMatrixReadOnly): DOMMatrixReadOnly
  {
    const zoom = 1 / this.coverScaleOf(matrix);

    return zoom <= CropArea.MAX_ZOOM + TOLERANCE
        ? matrix
        : this.scaledAbout(matrix, CropArea.MAX_ZOOM / zoom);
  }

  private scaledAbout(
      matrix: DOMMatrixReadOnly, factor: number): DOMMatrixReadOnly
  {
    const center = this.boundingBoxRegion.center;

    return new DOMMatrixReadOnly()
        .scale(factor, factor, 1, center.x, center.y)
        .multiply(matrix);
  }
}
